import React, { useMemo, useState } from "react";

import axios from "axios";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";

import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Select from "react-select";

import { getApiUrl } from "../../lib/common/getApiUrl";
import { useClientId } from "../Clients/Client/hooks/useClient";
import { reactSelectCustomStyles } from "../Employees/Employee/Information";
import { QUERY_BUILDINGS_FOR_ACCESS_LABELS } from "../../config/graphql/query";
import { MUTATION_CREATE_SYNGUARD_GENERIC_LABELS } from "../../config/graphql/mutation";

type LocationOption = { value: string; label: string };

const MAX_LABELS = 50;

const PrintForm: React.FC = () => {
  const { t } = useTranslation(["accessLabels", "common"]);
  const clientId = useClientId();

  const [count, setCount] = useState<number>(1);
  const [validFrom, setValidFrom] = useState<Date>(dayjs().toDate());
  const [validUntil, setValidUntil] = useState<Date>(dayjs().toDate());
  const [entityId, setEntityId] = useState<string>("");

  const [labelWidth, setLabelWidth] = useState<string>("");
  const [labelHeight, setLabelHeight] = useState<string>("");
  const [isPrinting, setIsPrinting] = useState(false);

  const { data: buildingsData } = useQuery<{ buildings: IBuilding[] }>(
    QUERY_BUILDINGS_FOR_ACCESS_LABELS,
    {
      context: { headers: { ClientId: clientId } },
      fetchPolicy: "network-only",
    },
  );

  const eligibleBuildings = useMemo(
    () =>
      (buildingsData?.buildings ?? []).filter(
        (b) => b.synguard?.accessProfile != null,
      ),
    [buildingsData],
  );

  const locationOptions = useMemo<LocationOption[]>(
    () => eligibleBuildings.map((b) => ({ value: b.id, label: b.title })),
    [eligibleBuildings],
  );

  const selectedLocation =
    locationOptions.find((o) => o.value === entityId) ?? null;

  const [createLabels] = useMutation(MUTATION_CREATE_SYNGUARD_GENERIC_LABELS, {
    context: { headers: { ClientId: clientId } },
    refetchQueries: ["synguardGenericLabels"],
  });

  const handlePrint = async () => {
    if (!count || count < 1 || count > MAX_LABELS) {
      toast.error<string>(t("accessLabels:form.validation.countRange"));
      return;
    }

    setIsPrinting(true);

    try {
      const result = await createLabels({
        variables: {
          input: {
            count,
            validFrom: dayjs(validFrom).toISOString(),
            validUntil: dayjs(validUntil).toISOString(),
            entity: entityId,
          },
        },
      });

      const payload = result.data?.createSynguardGenericLabels;
      const labels: ISynguardGenericLabel[] = payload?.labels ?? [];
      const failedCount: number = payload?.failedCount ?? 0;

      if (labels.length === 0) {
        toast.error<string>(t("accessLabels:toast.createFailed"));
        return;
      }

      if (failedCount > 0) {
        toast.warn<string>(
          t("accessLabels:toast.partialSuccess", {
            failedCount,
            count: failedCount,
          }),
        );
      }

      const ids = labels.map((l) => l.id).join(",");
      const token = localStorage.getItem("Authorization");

      const params = new URLSearchParams({ ids });
      if (labelWidth && Number(labelWidth) > 0) {
        params.set("width", labelWidth);
      }
      if (labelHeight && Number(labelHeight) > 0) {
        params.set("height", labelHeight);
      }

      const response = await axios.get(
        `${getApiUrl()}/api/v1/access-labels/print?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ClientId: clientId,
          },
          responseType: "blob",
        },
      );

      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );

      const printWindow = window.open(url, "_blank");

      if (printWindow) {
        setTimeout(() => printWindow.print(), 500);
      }

      toast.success<string>(
        t("accessLabels:toast.createSuccess", { count: labels.length }),
      );
    } catch (e) {
      toast.error<string>(t("accessLabels:toast.createError"));
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h6 className="card-title mb-3">{t("accessLabels:form.title")}</h6>
        <div className="form-row align-items-end">
          <div className="form-group col-md-2">
            <label htmlFor="al-count">{t("accessLabels:form.count")}</label>
            <input
              id="al-count"
              type="number"
              className="form-control"
              min={1}
              max={MAX_LABELS}
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="form-group col-md-2">
            <label htmlFor="al-valid-from" className="d-block">
              {t("accessLabels:form.validFrom")}
            </label>
            <DatePicker
              id="al-valid-from"
              className="form-control"
              selected={validFrom}
              onChange={(date: Date) => {
                if (!date) return;
                setValidFrom(date);
                if (dayjs(validUntil).isBefore(dayjs(date), "day")) {
                  setValidUntil(date);
                }
              }}
              minDate={new Date()}
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div className="form-group col-md-2">
            <label htmlFor="al-valid-until" className="d-block">
              {t("accessLabels:form.validUntil")}
            </label>
            <DatePicker
              id="al-valid-until"
              className="form-control"
              selected={validUntil}
              onChange={(date: Date) => date && setValidUntil(date)}
              minDate={validFrom}
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div className="form-group col-md-2">
            <label htmlFor="al-entity">{t("accessLabels:form.location")}</label>
            <Select<LocationOption>
              inputId="al-entity"
              options={locationOptions}
              value={selectedLocation}
              onChange={(option) => setEntityId(option?.value ?? "")}
              placeholder=""
              isClearable
              styles={reactSelectCustomStyles(false)}
            />
          </div>
          <div className="form-group col-md-2">
            <label htmlFor="al-label-width">
              {t("accessLabels:form.labelWidth")}
            </label>
            <input
              id="al-label-width"
              type="number"
              className="form-control"
              min={0}
              step="any"
              placeholder={t("accessLabels:form.optional")}
              value={labelWidth}
              onChange={(e) => setLabelWidth(e.target.value)}
            />
          </div>
          <div className="form-group col-md-2">
            <label htmlFor="al-label-height">
              {t("accessLabels:form.labelHeight")}
            </label>
            <input
              id="al-label-height"
              type="number"
              className="form-control"
              min={0}
              step="any"
              placeholder={t("accessLabels:form.optional")}
              value={labelHeight}
              onChange={(e) => setLabelHeight(e.target.value)}
            />
          </div>
        </div>
        <div className="form-row justify-content-center mt-2">
          <Button
            variant="primary"
            onClick={handlePrint}
            disabled={isPrinting || !entityId}
          >
            {isPrinting ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                {t("common:loading")}
              </>
            ) : (
              t("accessLabels:form.printButton")
            )}
          </Button>
        </div>
        {eligibleBuildings.length === 0 && buildingsData && (
          <p className="text-muted small mb-0">
            {t("accessLabels:form.noBuildings")}
          </p>
        )}
      </div>
    </div>
  );
};

export default PrintForm;
